import { ForceRefresh } from "@/reporting/index";
import { ApolloProvider } from "@apollo/client";
import { DesignSystemProvider } from "@zapier/design-system";
import { IdentityProvider, SessionProvider } from "@zapier/identity";
import { UserAgentContext } from "@zapier/responsive-utils";
import { NextIntlClientProvider } from "next-intl";
import type { AppProps } from "next/app";
import messages from "../../messages/en.json";
import { getOrCreateApolloClient } from "../apollo/getOrCreateApolloClient";
import { Unless, UserInfoProvider } from "../components";
import { routeIsInApp } from "../components/Link";
import { IntercomScript } from "../components/IntercomScript";
import { QualarooCodeSnippet } from "../components/QualarooCodeSnippet";
import { StylesReset } from "../components/StylesReset";
import {
  GoogleTagManagerScript,
  initializeGrafanaFaro,
  TrackRouting,
} from "../observability";
import { useNavigationMarks } from "../performance";
import { isDevelopment } from "../utils";

import "@zapier/design-system/style.css";
import "@zapier/design-tokens/custom-properties.css";
import "@zapier/spark-ui/style.css";
import "@zapier/zinnia-icons/style.css";

initializeGrafanaFaro();

type Props = AppProps<{ isEmbedPage: boolean }> & {
  userAgent: string;
};

export default function App({
  Component,
  pageProps,
  router,
  userAgent,
}: Props) {
  useNavigationMarks();

  const apolloClient = getOrCreateApolloClient();

  const isEmbedPage = router.pathname.startsWith("/embed/");

  return (
    <NextIntlClientProvider locale="en" messages={messages}>
      <Unless condition={isEmbedPage || isDevelopment()}>
        <GoogleTagManagerScript />
        <IntercomScript />
        <QualarooCodeSnippet />
      </Unless>

      <ApolloProvider client={apolloClient}>
        <StylesReset />
        <ForceRefresh />

        <IdentityProvider>
          <SessionProvider>
            <UserInfoProvider>
              <TrackRouting>
                <UserAgentContext.Provider value={userAgent}>
                  <DesignSystemProvider
                    navigate={(href) => {
                      // DS v10 Link prevents default anchor behavior before calling
                      // navigate, so we must handle ALL hrefs here. Use client-side
                      // routing for allowlisted internal paths; fall back to a full
                      // navigation for everything else (absolute URLs, external help
                      // links, mailto:, /webintent/*, non-allowlisted app routes, etc.)
                      const isAbsolute = /^(https?:)?\/\//.test(href);
                      if (routeIsInApp(href) && !isAbsolute) {
                        router.push(href);
                      } else {
                        window.location.assign(href);
                      }
                    }}
                  >
                    <Component {...pageProps} />
                  </DesignSystemProvider>
                </UserAgentContext.Provider>
              </TrackRouting>
            </UserInfoProvider>
          </SessionProvider>
        </IdentityProvider>
      </ApolloProvider>
    </NextIntlClientProvider>
  );
}
